import { defineStore } from "pinia";
import { getDashboards } from "@/services/apiCalls/marketSignal";
import {
  CATEGORY_ORDER,
  categoryLabel,
} from "@/services/marketSignalCategories";

/**
 * The Omni dashboards assigned to the user's company. Fetched once per session
 * (not persisted) and shared by the landing page, dashboard view, breadcrumb
 * and the navbar search.
 */
export const useMarketSignalStore = defineStore({
  id: "marketSignal",
  state: () => ({
    dashboards: [],
    loaded: false,
    loading: false,
    error: null,
  }),
  getters: {
    isEmpty() {
      return this.loaded && this.dashboards.length === 0;
    },
    byKey() {
      return (key) => this.dashboards.find((d) => d.key === key) || null;
    },
    /**
     * Dashboards grouped by access grant key, in product order, omitting
     * categories the company has no dashboards in. Unknown keys are appended
     * in first-seen order.
     */
    categories() {
      const keys = [...CATEGORY_ORDER];
      for (const d of this.dashboards) {
        if (!keys.includes(d.access_grant_key)) {
          keys.push(d.access_grant_key);
        }
      }
      return keys
        .map((key) => {
          const dashboards = this.dashboards.filter(
            (d) => d.access_grant_key === key
          );
          return {
            key,
            label: categoryLabel(key),
            dashboards,
            count: dashboards.length,
          };
        })
        .filter((c) => c.count > 0);
    },
    /**
     * Case-insensitive match on title and description, optionally limited to
     * one category. Returns dashboards in store (sort_order) order.
     */
    search() {
      return (term, categoryKey = null) => {
        const needle = (term || "").trim().toLowerCase();
        return this.dashboards.filter((d) => {
          if (categoryKey && d.access_grant_key !== categoryKey) {
            return false;
          }
          if (!needle) {
            return true;
          }
          const haystack = `${d.title || ""} ${d.description || ""}`;
          return haystack.toLowerCase().includes(needle);
        });
      };
    },
  },
  actions: {
    async load(force = false) {
      if (this.loading || (this.loaded && !force)) {
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        const data = await getDashboards();
        this.dashboards = data.dashboards || [];
        this.loaded = true;
      } catch (err) {
        this.error = err;
      } finally {
        this.loading = false;
      }
    },
  },
});
